import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, ChevronDown, HelpCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const QA = [
  {
    fr: ["Comment se passe un changement d'opérateur ?", "Je m'occupe de tout : souscription, résiliation de votre ancien contrat et suivi de l'activation. Vous n'avez aucune démarche à faire et aucune coupure prolongée."],
    en: ["How does switching operators work?", "I handle everything: signup, cancellation of your old contract and activation follow-up. You have zero paperwork and no extended downtime."],
  },
  {
    fr: ["Puis-je garder mon numéro de téléphone ?", "Oui, la portabilité est gratuite et automatique. Votre numéro est transféré en 1 à 2 jours ouvrables, sans interruption de service."],
    en: ["Can I keep my phone number?", "Yes, number portability is free and automatic. Your number is transferred within 1–2 working days without service interruption."],
  },
  {
    fr: ["Quels sont les délais d'installation pour l'internet ?", "En général 5 à 10 jours ouvrables selon la disponibilité du technicien dans votre région. Je suis le dossier de près et vous tiens informé à chaque étape."],
    en: ["How long does internet installation take?", "Usually 5–10 working days depending on technician availability in your area. I follow the file closely and keep you informed at every step."],
  },
  {
    fr: ["La promo « À VIE », c'est quoi exactement ?", "C'est une réduction permanente sur votre abonnement mobile tant que vous conservez votre pack Livebox + mobile chez Orange. L'offre actuelle est valable jusqu'au 4 octobre — après cette date, les conditions peuvent changer."],
    en: ["What exactly is the \"FOR LIFE\" promo?", "It's a permanent discount on your mobile subscription as long as you keep your Livebox + mobile pack at Orange. The current offer runs until October 4 — conditions may change after that date."],
  },
  {
    fr: ["Combien coûte votre accompagnement ?", "Rien du tout : mon service est 100% gratuit pour vous. Je suis rémunéré directement par Orange et Eneco en tant qu'agent indépendant — vous payez le même prix qu'en boutique, avec un suivi personnalisé en plus."],
    en: ["How much does your service cost?", "Nothing at all: my service is 100% free for you. I'm paid directly by Orange and Eneco as an independent agent — you pay the same price as in a store, with personal follow-up on top."],
  },
  {
    fr: ["Compteur mono-horaire ou bi-horaire : quelle différence ?", "Le mono-horaire applique un tarif unique toute la journée. Le bi-horaire a un tarif jour et un tarif nuit/week-end moins cher — intéressant si vous consommez surtout le soir. Mon simulateur Eneco gère les deux."],
    en: ["Single-rate or dual-rate meter: what's the difference?", "Single-rate applies one price all day. Dual-rate has a day price and a cheaper night/weekend price — worthwhile if you consume mostly in the evening. My Eneco simulator handles both."],
  },
  {
    fr: ["J'ai des panneaux solaires, est-ce pris en compte ?", "Oui ! Le simulateur intègre votre injection solaire dans le calcul de votre estimation Eneco pour un résultat au plus proche de votre vraie facture."],
    en: ["I have solar panels, is that taken into account?", "Yes! The simulator includes your solar injection in the Eneco estimate calculation, for a result as close as possible to your real bill."],
  },
  {
    fr: ["Comment fonctionne le programme de parrainage ?", "Réduction exclusive de 5% sur votre abonnement mobile, par mois et à vie, par référence qui souscrit un produit mobile Orange. 20 références = 100% de réduction sur un des numéros de votre facture mobile !"],
    en: ["How does the referral programme work?", "Exclusive discount of 5% off your mobile subscription, per month and for life, for each reference who signs up for an Orange mobile product. 20 references = 100% off one of the numbers on your mobile bill!"],
  },
  {
    fr: ["Comment prendre rendez-vous avec vous ?", "Réservez un créneau directement en ligne via le bouton « Être rappelé » : je vous appelle au moment choisi. Vous pouvez aussi me joindre au +32 473 22 33 44."],
    en: ["How do I book an appointment with you?", "Book a slot directly online via the \"Get a callback\" button: I call you at the chosen time. You can also reach me at +32 473 22 33 44."],
  },
  {
    fr: ["Dans quelle région intervenez-vous ?", "Partout en Belgique ! La plupart des dossiers se traitent à distance (téléphone, WhatsApp, e-mail) en français ou en anglais."],
    en: ["Which region do you cover?", "All of Belgium! Most files are handled remotely (phone, WhatsApp, e-mail) in French or English."],
  },
  {
    fr: ["Pouvez-vous analyser ma facture actuelle ?", "Oui : envoyez votre facture via le formulaire et je la compare aux offres actuelles Orange et Eneco pour chiffrer précisément vos économies possibles."],
    en: ["Can you analyse my current bill?", "Yes: send your bill via the form and I compare it against current Orange and Eneco offers to precisely quantify your possible savings."],
  },
  {
    fr: ["Et si j'ai un problème après la souscription ?", "Je reste votre interlocuteur unique après la signature : question sur la facture, souci technique, déménagement… un appel ou un message et je m'en occupe."],
    en: ["What if I have a problem after signing up?", "I remain your single point of contact after signing: billing question, technical issue, moving house… one call or message and I take care of it."],
  },
];

export const FAQ = () => {
  const { lang } = useLang();
  const fr = lang === "fr";
  const [q, setQ] = useState("");
  const [openIdx, setOpenIdx] = useState(0);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: QA.map((item) => ({
        "@type": "Question",
        name: item.fr[0],
        acceptedAnswer: { "@type": "Answer", text: item.fr[1] },
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return QA.map((item, i) => ({ item, i })).filter(({ item }) => {
      if (!needle) return true;
      const [question, answer] = fr ? item.fr : item.en;
      return (question + " " + answer).toLowerCase().includes(needle);
    });
  }, [q, fr]);

  return (
    <section id="faq" className="py-24 md:py-32 border-t border-white/10 bg-[#0d0d0d]" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FF7900] mb-4"
        >
          <HelpCircle className="w-4 h-4" /> FAQ
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-10"
        >
          {fr ? "Questions fréquentes" : "Frequently asked questions"}
        </motion.h2>

        <div className="relative mb-10">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={fr ? "Rechercher une question… (numéro, installation, parrainage…)" : "Search a question… (number, installation, referral…)"}
            data-testid="faq-search"
            className="w-full bg-black/40 border border-white/15 pl-11 pr-4 py-3.5 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300"
          />
        </div>

        {filtered.length === 0 && (
          <p className="text-sm text-white/40" data-testid="faq-no-results">
            {fr ? "Aucune réponse trouvée — posez votre question au chat ou appelez le +32 473 22 33 44 !" : "No answer found — ask the chat or call +32 473 22 33 44!"}
          </p>
        )}

        <div className="divide-y divide-white/10 border-y border-white/10">
          {filtered.map(({ item, i }) => {
            const [question, answer] = fr ? item.fr : item.en;
            const open = openIdx === i;
            return (
              <div key={i} data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  data-testid={`faq-question-${i}`}
                  className="w-full flex items-center justify-between gap-4 py-5 text-left group"
                >
                  <span className={`text-sm md:text-base font-medium transition-colors duration-300 ${open ? "text-[#FF7900]" : "group-hover:text-[#FF7900]"}`}>
                    {question}
                  </span>
                  <ChevronDown className={`w-4 h-4 shrink-0 text-white/40 transition-transform duration-300 ${open ? "rotate-180 text-[#FF7900]" : ""}`} />
                </button>
                <div className={`grid transition-[grid-template-rows] duration-300 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                  <div className="overflow-hidden">
                    <p className="pb-5 text-sm text-white/55 leading-relaxed max-w-2xl" data-testid={`faq-answer-${i}`}>
                      {answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
